import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Users, UserCircle, Calendar, Trophy, TrendingUp, ArrowRight, Shield, Target, FileText, BarChart3, Award, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardPage() {
  const { token, organization, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkedOrg, setCheckedOrg] = useState(false);
  const [awards, setAwards] = useState({ mvp_a45: '', best_keeper_a45: '', best_coach_a45: '', tournament_mvp: '' });
  const [savingAwards, setSavingAwards] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!organization) {
      navigate('/onboarding', { replace: true });
      return;
    }
    setCheckedOrg(true);
    fetchDashboardStats();
    fetchAwards();
  }, [organization, authLoading, navigate]);

  const fetchAwards = async () => {
    if (!organization) return;
    try {
      const res = await fetch(`${API_URL}/api/awards/${organization.org_id}`);
      if (res.ok) {
        const data = await res.json();
        setAwards({ mvp_a45: data.mvp_a45 || '', best_keeper_a45: data.best_keeper_a45 || '', best_coach_a45: data.best_coach_a45 || '', tournament_mvp: data.tournament_mvp || '' });
      }
    } catch {}
  };

  const saveAwards = async () => {
    setSavingAwards(true);
    try {
      const res = await fetch(`${API_URL}/api/awards/${organization.org_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(awards)
      });
      if (res.ok) toast.success('Awards saved');
      else toast.error('Failed to save awards');
    } catch { toast.error('Failed'); }
    finally { setSavingAwards(false); }
  };

  const fetchDashboardStats = async () => {
    if (!organization) return;
    try {
      const response = await fetch(`${API_URL}/api/dashboard/${organization.org_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  // Show loading while auth is checking or org hasn't been verified yet
  if (authLoading || (!checkedOrg && !organization)) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  const statCards = [
    { label: t('dashboard.stats.teams'), value: stats?.total_teams || 0, icon: Users, color: 'emerald', path: '/teams' },
    { label: t('dashboard.stats.players'), value: stats?.total_players || 0, icon: UserCircle, color: 'blue', path: '/players' },
    { label: t('dashboard.stats.matches'), value: stats?.total_matches || 0, icon: Calendar, color: 'orange', path: '/matches' },
    { label: t('dashboard.stats.completed'), value: stats?.completed_matches || 0, icon: Trophy, color: 'purple', path: '/matches' },
  ];

  return (
    <DashboardLayout>
      <div data-testid="dashboard-page" className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-black uppercase tracking-tight">
            {t('dashboard.title')}
          </h1>
          <p className="text-gray-600 mt-2">{t('dashboard.subtitle')}</p>
        </div>

        {/* Quick Navigation Bar */}
        <div className="bg-white border border-gray-200 rounded-xl p-2 flex flex-wrap gap-2" data-testid="dashboard-quick-nav">
          {[
            { path: '/tournament-info', label: t('nav.tournamentInfo'), icon: FileText },
            { path: '/standings', label: t('nav.standings'), icon: Trophy },
            { path: '/matches', label: t('nav.matches'), icon: Calendar },
            { path: '/teams', label: t('nav.teams'), icon: Users },
            { path: '/players', label: t('nav.players'), icon: UserCircle },
            { path: '/stats', label: t('nav.playerStats'), icon: BarChart3 },
            { path: '/sponsors', label: t('nav.sponsors'), icon: Award },
            { path: '/referees', label: t('nav.referees'), icon: Shield },
          ].map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-600 hover:text-black hover:bg-gray-100 transition-colors"
              data-testid={`quick-nav-${item.path.slice(1)}`}
            >
              <item.icon size={16} />
              <span>{item.label}</span>
            </button>
          ))}
        </div>

        {/* Champion Banner */}
        {stats?.champion ? (
          <div className="bg-gradient-to-r from-yellow-500/10 via-yellow-500/5 to-emerald-500/10 border-2 border-yellow-500/30 rounded-xl p-6" data-testid="champion-banner">
            <div className="flex items-center gap-5">
              {stats.champion.logo ? (
                <img src={stats.champion.logo} alt="" className="w-20 h-20 object-contain rounded-lg border border-yellow-500/20 bg-white p-1" />
              ) : (
                <div className="w-20 h-20 bg-yellow-500/10 rounded-lg flex items-center justify-center">
                  <Trophy className="text-yellow-500" size={40} />
                </div>
              )}
              <div className="flex-1">
                <p className="text-yellow-600 text-xs font-bold uppercase tracking-widest">CNVT 2026 Champions</p>
                <p className="text-black font-bold text-3xl mt-1">{stats.champion.team_name}</p>
                <p className="text-gray-600 text-sm mt-1">
                  Final: {stats.champion.final_score} vs {stats.champion.opponent}
                  {stats.champion.penalties && <span className="text-amber-600"> (Pen: {stats.champion.penalties})</span>}
                </p>
              </div>
              <Trophy className="text-yellow-500 shrink-0" size={48} />
            </div>
          </div>
        ) : organization && (
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4 flex items-center gap-3">
            <Trophy className="text-emerald-500" size={24} />
            <span className="text-black">
              {t('dashboard.welcome')} <span className="font-semibold">{organization.tournament_name}</span>!
            </span>
          </div>
        )}

        {/* Leading Scorer Highlight */}
        {stats?.top_scorers?.[0]?.goals > 0 && (
          <div className="bg-gradient-to-r from-yellow-500/10 to-emerald-500/10 border border-yellow-500/20 rounded-lg p-4 flex items-center gap-4">
            <Target className="text-yellow-500 shrink-0" size={28} />
            <div>
              <p className="text-gray-600 text-xs uppercase tracking-wider">Leading Goal Scorer</p>
              <p className="text-black font-bold text-lg">
                {stats.top_scorers[0].name} <span className="text-gray-600 font-normal text-sm">({stats.top_scorers[0].team_name})</span>
              </p>
            </div>
            <span className="ml-auto text-yellow-500 font-bold text-3xl">{stats.top_scorers[0].goals} goals</span>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, index) => (
            <Card 
              key={index} 
              className="bg-white border-gray-200 card-hover"
              data-testid={`stat-${stat.label.toLowerCase()}`}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-xs uppercase tracking-wider">{stat.label}</p>
                    <p className="text-3xl font-bold text-black mt-1">{stat.value}</p>
                  </div>
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center bg-${stat.color}-500/10`}>
                    <stat.icon className={`text-${stat.color}-500`} size={24} />
                  </div>
                </div>
                <button 
                  onClick={() => navigate(stat.path)}
                  className="flex items-center gap-1 text-emerald-500 text-sm mt-4 hover:text-emerald-400 transition-colors"
                >
                  {t('dashboard.viewDetails')} <ArrowRight size={14} />
                </button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tournament Stats Row */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
            <p className="text-gray-500 text-[10px] uppercase tracking-wider">Total Goals</p>
            <p className="text-black font-bold text-2xl mt-1">{stats?.total_goals || 0}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
            <p className="text-yellow-400 text-[10px] uppercase tracking-wider">Yellow Cards</p>
            <p className="text-yellow-400 font-bold text-2xl mt-1">{stats?.total_yellow_cards || 0}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
            <p className="text-red-500 text-[10px] uppercase tracking-wider">Red Cards</p>
            <p className="text-red-500 font-bold text-2xl mt-1">{stats?.total_red_cards || 0}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
            <p className="text-gray-500 text-[10px] uppercase tracking-wider">Mins Played</p>
            <p className="text-black font-bold text-2xl mt-1">{stats?.total_mins_played || 0}</p>
          </div>
          {stats?.most_offensive && (
            <div className="bg-white border border-emerald-500/20 rounded-lg p-4 text-center">
              <p className="text-emerald-400 text-[10px] uppercase tracking-wider">Most Offensive</p>
              <p className="text-black font-bold text-sm mt-1 truncate">{stats.most_offensive.team_name}</p>
              <p className="text-emerald-400 text-xs">{stats.most_offensive.goals} goals</p>
            </div>
          )}
          {stats?.most_defensive && (
            <div className="bg-white border border-blue-500/20 rounded-lg p-4 text-center">
              <p className="text-blue-400 text-[10px] uppercase tracking-wider">Most Defensive</p>
              <p className="text-black font-bold text-sm mt-1 truncate">{stats.most_defensive.team_name}</p>
              <p className="text-blue-400 text-xs">{stats.most_defensive.goals_conceded} conceded</p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Matches */}
          <div className="lg:col-span-2">
            <Card className="bg-white border-gray-200">
              <CardHeader className="border-b border-gray-200">
                <CardTitle className="font-heading text-xl text-black uppercase flex items-center justify-between">
                  {t('dashboard.recentMatches')}
                  <button 
                    onClick={() => navigate('/matches')}
                    className="text-sm text-emerald-500 hover:text-emerald-400 font-normal normal-case flex items-center gap-1"
                  >
                    {t('common.viewAll')} <ArrowRight size={14} />
                  </button>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {stats?.recent_matches?.length > 0 ? (
                  <div className="divide-y divide-gray-100">
                    {stats.recent_matches.map((match, index) => (
                      <div 
                        key={match.match_id || index}
                        className="p-4 flex items-center justify-between table-row-hover"
                      >
                        <div className="flex items-center gap-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium uppercase ${
                            match.status === 'completed' ? 'badge-win' : 'badge-scheduled'
                          }`}>
                            {match.stage}
                          </span>
                          <div className="flex items-center gap-3">
                            <span className="text-black font-medium">{match.home_team_name}</span>
                            <span className="text-gray-500">vs</span>
                            <span className="text-black font-medium">{match.away_team_name}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          {match.status === 'completed' ? (
                            <span className="font-mono text-lg text-black">
                              {match.home_score} - {match.away_score}
                            </span>
                          ) : (
                            <span className="text-gray-500 text-sm">
                              {match.match_date} • {match.match_time}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    {t('dashboard.noMatches')}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* MVP Leaders */}
          <Card className="bg-white border-gray-200">
            <CardHeader className="border-b border-gray-200">
              <CardTitle className="font-heading text-xl text-black uppercase flex items-center gap-2">
                <TrendingUp className="text-emerald-500" size={20} />
                {t('dashboard.topScorers')}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {stats?.top_scorers?.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {stats.top_scorers.map((player, index) => (
                    <div 
                      key={player.player_id || index}
                      className="p-4 flex items-center justify-between table-row-hover"
                    >
                      <div className="flex items-center gap-3">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          index === 0 ? 'bg-yellow-500 text-black' :
                          index === 1 ? 'bg-gray-400 text-black' :
                          index === 2 ? 'bg-orange-600 text-black' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {index + 1}
                        </span>
                        <div>
                          <p className="text-black font-medium">{player.name}</p>
                          <p className="text-gray-500 text-xs">{player.team_name}</p>
                        </div>
                      </div>
                      <span className="text-emerald-500 font-bold text-xl">{player.goals}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-gray-500">
                  {t('dashboard.noStats')}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Most Disciplined Teams */}
          <Card className="bg-white border-gray-200">
            <CardHeader className="border-b border-gray-200">
              <CardTitle className="font-heading text-xl text-black uppercase flex items-center gap-2">
                <Shield className="text-emerald-500" size={20} />
                Fair Play Ranking
              </CardTitle>
              <div className="flex items-center justify-between mt-1">
                <p className="text-gray-500 text-xs">Start: 1000 coins | YC: -10 | RC: -30 | Clean game: +20</p>
                {organization && (
                  <a
                    href={`${API_URL}/api/export/disciplinary/${organization.org_id}`}
                    download
                    className="inline-flex items-center gap-1.5 px-3 py-1 bg-red-50 border border-red-200 text-red-600 hover:bg-red-100 rounded text-xs transition-colors"
                    data-testid="export-disciplinary-btn"
                  >
                    <FileText size={12} /> Export Fines Report
                  </a>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {stats?.most_disciplined?.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {stats.most_disciplined.map((team, index) => (
                    <div key={index} className="p-4 flex items-center justify-between table-row-hover">
                      <div className="flex items-center gap-3">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          index === 0 ? 'bg-emerald-500 text-black' : index === 1 ? 'bg-blue-500 text-black' : index === 2 ? 'bg-orange-500 text-black' : 'bg-gray-100 text-gray-600'
                        }`}>{index + 1}</span>
                        <div>
                          <p className="text-black font-medium">{team.team_name}</p>
                          <p className="text-gray-500 text-xs">
                            {team.yellow_cards} YC | {team.red_cards} RC | {team.clean_games || 0} clean
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`font-bold text-lg ${team.coins >= 1000 ? 'text-emerald-500' : team.coins >= 950 ? 'text-yellow-400' : 'text-orange-400'}`}>
                          {team.coins}
                        </span>
                        <p className="text-gray-500 text-xs">coins</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-gray-500">
                  No fair play data yet
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Tournament MVP */}
        <Card className="bg-white border-yellow-200">
          <CardHeader className="border-b border-yellow-200 bg-yellow-50">
            <CardTitle className="font-heading text-xl text-black uppercase flex items-center gap-2">
              <Award className="text-yellow-500" size={20} />
              Tournament MVP — CNVT 2026
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="max-w-sm">
              <label className="text-gray-600 text-xs uppercase tracking-wider block mb-1">Most Valuable Player</label>
              <Input
                value={awards.tournament_mvp}
                onChange={(e) => setAwards(prev => ({ ...prev, tournament_mvp: e.target.value }))}
                placeholder="Enter Tournament MVP name"
                className="bg-gray-50 border-gray-200 text-black text-lg"
                data-testid="award-tournament-mvp"
              />
            </div>
            <Button onClick={saveAwards} disabled={savingAwards} className="mt-4 bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="save-mvp-btn">
              <Save size={14} className="mr-1" /> {savingAwards ? 'Saving...' : 'Save MVP'}
            </Button>
          </CardContent>
        </Card>

        {/* A45 Gala Match Awards */}
        <Card className="bg-white border-gray-200">
          <CardHeader className="border-b border-gray-200">
            <CardTitle className="font-heading text-xl text-black uppercase flex items-center gap-2">
              <Award className="text-yellow-500" size={20} />
              Gala Match Awards — Above 45 Years
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="text-gray-600 text-xs uppercase tracking-wider block mb-1">MVP A45</label>
                <Input
                  value={awards.mvp_a45}
                  onChange={(e) => setAwards(prev => ({ ...prev, mvp_a45: e.target.value }))}
                  placeholder="Enter MVP name"
                  className="bg-gray-50 border-gray-200 text-black"
                  data-testid="award-mvp-a45"
                />
              </div>
              <div>
                <label className="text-gray-600 text-xs uppercase tracking-wider block mb-1">Best A45 Keeper</label>
                <Input
                  value={awards.best_keeper_a45}
                  onChange={(e) => setAwards(prev => ({ ...prev, best_keeper_a45: e.target.value }))}
                  placeholder="Enter best keeper name"
                  className="bg-gray-50 border-gray-200 text-black"
                  data-testid="award-keeper-a45"
                />
              </div>
              <div>
                <label className="text-gray-600 text-xs uppercase tracking-wider block mb-1">Best A45 Coach</label>
                <Input
                  value={awards.best_coach_a45}
                  onChange={(e) => setAwards(prev => ({ ...prev, best_coach_a45: e.target.value }))}
                  placeholder="Enter best coach name"
                  className="bg-gray-50 border-gray-200 text-black"
                  data-testid="award-coach-a45"
                />
              </div>
            </div>
            <Button onClick={saveAwards} disabled={savingAwards} className="mt-4 bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="save-awards-btn">
              <Save size={14} className="mr-1" /> {savingAwards ? 'Saving...' : 'Save Awards'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
